import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, ChevronDown, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { useToaster } from "../../contexts/ToasterContext";
import { DataTable } from "../../components/ui/data-table";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../../components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

// Import our new hooks and utilities
import { useIntegrationFindings } from '../../hooks/useIntegrationFindings';
import { useIntegrationActions } from '../../hooks/useIntegrationActions';
import { useIntegrationFilters } from '../../hooks/useIntegrationFilters';
import {
  getIntegrationConfig,
  getBulkActions
} from '../../config/integrationConfig';
import {
  getFindingId,
  getEmailOnly,
  formatFilePath,
  getStatusInfo,
  getSeverityInfo,
  getVerificationInfo,
  formatDate,
  getIconComponent
} from '../../utils/integrationHelpers';

// Simple tooltip component
const Tooltip = ({ content, children }) => {
  if (!content) return children;
  return (
    <span className="relative group inline-flex items-center">
      {children}
      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block whitespace-nowrap rounded-md bg-white border border-[#C0ACF3] px-2 py-1 text-[11px] font-medium text-[#000000] shadow-lg z-50">
        {content}
      </span>
    </span>
  );
};

const TableShimmer = ({ rows = 8 }) => {
  return (
    <div className="overflow-hidden border border-gray-300 rounded-lg">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left p-4 text-gray-700 font-medium w-16">
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
            </th>
            <th className="text-left p-4 text-gray-700 font-medium w-1/7">Source</th>
            <th className="text-left p-4 text-gray-700 font-medium w-1/7">Category</th>
            <th className="text-left p-4 text-gray-700 font-medium w-1/7">Details</th>
            <th className="text-left p-4 text-gray-700 font-medium w-1/7">File</th>
            <th className="text-left p-4 text-gray-700 font-medium w-1/7">Author</th>
            <th className="text-left p-4 text-gray-700 font-medium w-1/7">Actions</th>
            <th className="text-left p-4 text-gray-700 font-medium w-1/7">Status</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, index) => (
            <tr key={index} className={index < rows - 1 ? "border-b border-gray-100" : ""}>
              <td className="p-4 text-center w-16">
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse mx-auto"></div>
              </td>
              <td className="p-4 w-1/7">
                <div className="w-20 h-6 bg-gray-200 rounded-full animate-pulse"></div>
              </td>
              <td className="p-4 w-1/7">
                <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
              </td>
              <td className="p-4 w-1/7">
                <div className="flex items-center gap-2">
                  <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </td>
              <td className="p-4 w-1/7">
                <div className="flex items-center gap-2">
                  <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </td>
              <td className="p-4 w-1/7">
                <div className="flex items-center gap-2">
                  <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </td>
              <td className="p-4 w-1/7">
                <div className="w-20 h-8 bg-gray-200 rounded animate-pulse"></div>
              </td>
              <td className="p-4 w-1/7">
                <div className="w-24 h-6 bg-gray-200 rounded-full animate-pulse"></div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const FilterShimmer = () => {
  return (
    <div className="px-0 py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center space-x-2">
          <div className="h-8 w-20 bg-gray-300 rounded animate-pulse"></div>
          <div className="h-8 w-24 bg-gray-300 rounded animate-pulse"></div>
          <div className="h-8 w-28 bg-gray-300 rounded animate-pulse"></div>
          <div className="h-8 w-32 bg-gray-300 rounded animate-pulse"></div>
        </div>
        <div className="shrink-0 h-10 w-32 bg-gray-300 rounded animate-pulse"></div>
      </div>
    </div>
  );
};

// Confirmation Dialog Component
const ConfirmationDialog = ({ show, type, onConfirm, onCancel }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {type === 'findings' ? 'Confirm Ignore Finding' : 
               type === 'unignore' ? 'Confirm Unignore Finding' : 'Confirm Action'}
            </h3>
          </div>
        </div>
        <div className="mb-6">
          <p className="text-sm text-gray-500">
            {type === 'findings'
              ? 'Are you sure you want to ignore this finding? This action will mark the finding as ignored and it will no longer appear in active findings.'
              : type === 'unignore'
              ? 'Are you sure you want to unignore this finding? This action will mark the finding as active and it will appear in active findings again.'
              : 'Are you sure you want to perform this action?'}
          </p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C0ACF3]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            {type === 'findings' ? 'Yes, Ignore Finding' : 
             type === 'unignore' ? 'Yes, Unignore Finding' : 'Yes, Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

const IntegrationListPage = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToaster();

  const integrationType = type;
  const integrationId = id;
  
  // Get integration configuration
  const integrationConfig = getIntegrationConfig(integrationType);
  
  // Configuration fetching for validation
  const [config, setConfig] = useState({});
  
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogType, setConfirmDialogType] = useState('');
  const [findingToProcess, setFindingToProcess] = useState(null);

  // Use our new hooks
  const filtersHook = useIntegrationFilters(integrationType, integrationId, integrationConfig);
  const findingsHook = useIntegrationFindings(integrationType, integrationId, integrationConfig, showToast, filtersHook);
  const actionsHook = useIntegrationActions(
    integrationType,
    integrationId,
    integrationConfig,
    showToast,
    findingsHook.loadFindings,
    findingsHook.getFindingId,
    findingsHook.setSelected,
    findingsHook.setRowErrors,
    config,
    findingsHook.updateFindingStatus
  );


  // Effects - Fetch config and filter options on mount and when integration changes
  useEffect(() => {
    if (integrationType && integrationId && integrationConfig) {
      // Mock config data
      const loadConfig = async () => {
        try {
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Mock config data
          const mockConfig = {
            jira_url: "https://mock-jira.example.com",
            freshservice_url: "https://mock-freshservice.example.com",
            hashicorp_vault_url: "https://mock-vault.example.com",
            github_token: "ghp_mock123456789",
            aws_access_key: "AKIAMOCK123456",
            azure_client_id: "mock-azure-client-id"
          };
          setConfig(mockConfig);
        } catch (error) {
          console.error("Error fetching config:", error);
        }
      };

      loadConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationType, integrationId, integrationConfig]);

  // Create dynamic columns based on integration config
  const createColumns = () => {
    if (!integrationConfig?.findingColumns) {
      return [];
    }

    const baseColumns = [];
    
    // Only add checkbox column if showCheckboxes is not false
    if (integrationConfig?.features?.showCheckboxes !== false) {
      baseColumns.push({
        id: "select",
        header: () => (
          <div className="flex items-center justify-start gap-1">
            <input
              ref={findingsHook.selectAllCheckboxRef}
              type="checkbox"
              aria-label="Select all"
              className="h-4 w-4 rounded border-gray-300 text-[#44087D] accent-[#44087D] focus:ring-[#44087D] focus:ring-2"
              checked={findingsHook.allSelected}
              onChange={findingsHook.toggleSelectAll}
            />
            <span className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 rounded-full bg-gray-100 text-gray-700 text-xs">
              {findingsHook.selectedCount}
            </span>
          </div>
        ),
        cell: (_, row) => {
          const rowId = row.id || String(row.scan_id || '');
          const isSelected = !!findingsHook.selected[rowId];
          const hasError = !!findingsHook.rowErrors[rowId];
          return (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                aria-label={`Select finding ${rowId}`}
                className="h-4 w-4 rounded border-gray-300 text-[#44087D] accent-[#44087D] focus:ring-[#44087D] focus:ring-2"
                checked={isSelected}
                onChange={() => findingsHook.toggleSelect(rowId)}
              />
              {hasError && (
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-4 h-4 text-red-500" title="Error occurred" />
                </div>
              )}
            </div>
          );
        },
        headerClassName: "w-16",
        cellClassName: "text-center"
      });
    }

    // Create dynamic columns from config
    const dynamicColumns = integrationConfig.findingColumns.map(colConfig => {
      const column = {
        accessorKey: colConfig.accessorKey,
        header: colConfig.header,
        headerClassName: "w-1/7",
        cellClassName: "w-1/7"
      };

      // Cell rendering based on cell type
      switch (colConfig.cell) {
        case 'detector_badge':
          column.cell = (_, row) => (
            <span className="text-sm text-gray-900">
              {row[colConfig.accessorKey] || 'Unknown'}
            </span>
          );
          break;

        case 'verification_status':
          column.cell = (_, row) => {
            // Check if this is repositories or s3 integration to use specific styling
            if (integrationType === 'github' || integrationType === 's3') {
              return row.verified ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  Unvalidated
                </span>
              );
            } else {
              // Use generic verification info for other integrations
              const verificationInfo = getVerificationInfo(row.verified);
              return (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${verificationInfo.className}`}>
                  {verificationInfo.label}
                </span>
              );
            }
          };
          break;

        case 'truncated_text':
          column.cell = (_, row, index) => {
            const value = row[colConfig.accessorKey];
            if (!value) {
              return <span className="font-mono text-xs text-gray-500">N/A</span>;
            }
            
            const rowId = getFindingId(row, index);
            const isRevealed = findingsHook.revealedRows[rowId];
            const maxLength = Math.min(colConfig.maxLength || 40, value.length);
            const displayValue = isRevealed ? value.substring(0, maxLength) : '•'.repeat(maxLength);
            
            return (
              <Tooltip content={isRevealed ? value : 'Click to reveal'}>
                <button
                  onClick={() => findingsHook.toggleReveal(rowId)}
                  className="inline-flex items-center gap-1.5 text-left hover:opacity-80 transition-opacity"
                  aria-label={isRevealed ? "Hide details" : "Show details"}
                >
                  <span className="font-mono text-xs text-gray-900 inline-block max-w-[200px]">
                    {displayValue}
                  </span>
                  {isRevealed ? <EyeOff className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" /> : <Eye className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />}
                </button>
              </Tooltip>
            );
          };
          break;

        case 'file_link':
          column.cell = (_, row) => {
            const link = row[colConfig.accessorKey] || row.link;
            return link ? (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gray-900 hover:text-gray-700"
                title="Open in external source"
              >
                <span className="text-sm">
                  View File
                </span>
                <ExternalLink className="w-4 h-4 text-gray-500" />
              </a>
            ) : (
              <span className="text-sm text-gray-500">-</span>
            );
          };
          break;

        case 'file_path':
          column.cell = (_, row) => {
            const value = row[colConfig.accessorKey];
            const maxLength = colConfig.maxLength || 50;
            const formattedPath = formatFilePath(value, maxLength);
            
            return (
              <Tooltip content={value && value.length > maxLength ? value : null}>
                <span className="text-sm text-gray-900">
                  {formattedPath}
                </span>
              </Tooltip>
            );
          };
          break;

        case 'email':
          column.cell = (_, row) => {
            const emailOnly = getEmailOnly(row[colConfig.accessorKey]);
            return (
              <span className="text-sm text-gray-900">
                {emailOnly}
              </span>
            );
          };
          break;

        case 'severity_badge':
          column.cell = (_, row) => {
            const severityInfo = getSeverityInfo(row[colConfig.accessorKey]);
            return (
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${severityInfo.bg} ${severityInfo.text}`}>
                {severityInfo.label}
              </span>
            );
          };
          break;

        case 'date':
          column.cell = (_, row) => (
            <span className="text-sm text-gray-900">
              {formatDate(row[colConfig.accessorKey])}
            </span>
          );
          break;

        case 'text':
          column.cell = (_, row) => (
            <span className="text-sm text-gray-900">
              {row[colConfig.accessorKey] || '-'}
            </span>
          );
          break;

        case 'truncated_text_with_tooltip':
          column.cell = (_, row) => {
            const value = row[colConfig.accessorKey];
            if (!value) {
              return <span className="text-sm text-gray-500">-</span>;
            }
            
            const maxLength = colConfig.maxLength || 20;
            const shouldTruncate = value.length > maxLength;
            const displayValue = shouldTruncate ? `${value.substring(0, maxLength)}...` : value;
            
            return (
              <Tooltip content={shouldTruncate ? value : null}>
                <span className="text-sm text-gray-900">
                  {displayValue}
                </span>
              </Tooltip>
            );
          };
          break;

        case 'truncated_email_with_tooltip':
          column.cell = (_, row) => {
            const emailOnly = getEmailOnly(row[colConfig.accessorKey]);
            if (!emailOnly) {
              return <span className="text-sm text-gray-500">-</span>;
            }
            
            const maxLength = colConfig.maxLength || 20;
            const shouldTruncate = emailOnly.length > maxLength;
            const displayValue = shouldTruncate ? `${emailOnly.substring(0, maxLength)}...` : emailOnly;
            
            return (
              <Tooltip content={shouldTruncate ? emailOnly : null}>
                <span className="text-sm text-gray-900">
                  {displayValue}
                </span>
              </Tooltip>
            );
          };
          break;

        case 's3_insights':
          column.cell = (_, row, index) => {
            const findingId = getFindingId(row, index);
            const hasInsights = row && row.has_insights === true;
            const isDisabled = !row.verified || !hasInsights;
            const tooltipMessage = !row.verified 
              ? "Insights are only available for validated findings"
              : "Information not available from Source";
            
            const handleInsightsClick = () => {
              navigate(`/integration/s3/${integrationId}/blast-radius/${findingId}`);
            };
            
            const button = (
              <button
                onClick={handleInsightsClick}
                disabled={isDisabled}
                className={`flex items-center gap-2 transition-colors text-sm ${
                  isDisabled
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-900 hover:text-gray-700'
                }`}
              >
                <span>{isDisabled ? 'Insights*' : 'Insights'}</span>
                <ExternalLink className={`w-4 h-4 ${isDisabled ? 'text-gray-300' : 'text-gray-500'}`} />
              </button>
            );
            
            if (isDisabled) {
              return (
                <Tooltip content={tooltipMessage}>{button}</Tooltip>
              );
            }
            return button;
          };
          break;

        case 'repo_insights':
          column.cell = (_, row, index) => {
            const findingId = getFindingId(row, index);
            const hasInsights = row && row.has_insights === true;
            const isDisabled = !row.verified || !hasInsights;
            const tooltipMessage = !row.verified 
              ? "Insights are only available for validated findings"
              : "Information not available from Source";
            
            const handleInsightsClick = () => {
              navigate(`/integration/github/${integrationId}/blast-radius/${findingId}`);
            };
            
            const button = (
              <button
                onClick={handleInsightsClick}
                disabled={isDisabled}
                className={`flex items-center gap-2 transition-colors text-sm ${
                  isDisabled
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-900 hover:text-gray-700'
                }`}
              >
                <span>{isDisabled ? 'Insights*' : 'Insights'}</span>
                <ExternalLink className={`w-4 h-4 ${isDisabled ? 'text-gray-300' : 'text-gray-500'}`} />
              </button>
            );
            
            if (isDisabled) {
              return (
                <Tooltip content={tooltipMessage}>{button}</Tooltip>
              );
            }
            return button;
          };
          break;

        default:
          column.cell = (_, row) => (
            <span className="text-sm text-gray-900">
              {row[colConfig.accessorKey] || '-'}
            </span>
          );
      }

      return column;
    });

    // Add actions column if actions are configured and showActions is not false
    const actionsColumn = {
      id: "actions",
      header: "Actions",
      cell: (_, row, index) => {
        if (integrationConfig?.features?.showActions === false) {
          return null;
        }
        const findingId = getFindingId(row, index);
        
        if (!integrationConfig.actions || integrationConfig.actions.length === 0) {
          return <span className="text-sm text-gray-500">-</span>;
        }

        return (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  disabled={actionsHook.actionLoading[findingId] || findingsHook.selectedCount > 0}
                  className="flex items-center space-x-2 px-3 py-2 border border-[#441a9a] rounded text-xs transition-colors disabled:opacity-50 bg-[#F4F0FD] text-[#441a9a] hover:bg-[#E8E0F7]"
                >
                  {actionsHook.actionLoading[findingId] ? (
                    <div className="animate-spin rounded-full h-3 w-3 border border-[#441a9a] border-t-transparent"></div>
                  ) : (
                    <>
                      <span className="truncate">
                        Actions
                      </span>
                      <ChevronDown className="w-3 h-3 flex-shrink-0" />
                    </>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white">
                {integrationConfig.actions
                  .filter(actionConfig => {
                    // Filter actions based on finding state (for github integration)
                    if (integrationType === 'github' && actionConfig.condition) {
                      const isIgnored = row.status === 'ignore';
                      const hasJiraTicket = !!(row.jirakey || row.jira_key || row.jiraKey);
                      const isResolved = row.status === 'Done' ||
                                        row.jira_status === 'Done' ||
                                        row.status === 'DONE' ||
                                        row.jira_status === 'DONE' ||
                                        row.jira_status === 'RESOLVED';
                      
                      switch (actionConfig.condition) {
                        case 'normal':
                          return !hasJiraTicket && !isResolved && !isIgnored;
                        case 'hasTicket':
                          return hasJiraTicket && !isResolved && !isIgnored;
                        case 'ignored':
                          return isIgnored;
                        case 'all':
                          return true;
                        default:
                          return true;
                      }
                    }
                    // For other integrations or actions without conditions, show all actions
                    return true;
                  })
                  .map(actionConfig => {
                    const IconComponent = getIconComponent(actionConfig.icon);
                    
                    return (
                      <DropdownMenuItem
                        key={actionConfig.key}
                        onClick={() => handleAction(actionConfig, row, index)}
                        className="flex items-center space-x-2 text-xs cursor-pointer text-[#000000] hover:bg-[#F4F0FD] focus:bg-[#F4F0FD] focus:text-[#000000]"
                      >
                        <IconComponent className="w-3 h-3" />
                        <span>{actionConfig.label}</span>
                      </DropdownMenuItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      headerClassName: "w-1/7",
      cellClassName: "w-1/7"
    };

    // Add status column
    const statusColumn = {
      id: "status",
      header: "Status",
      cell: (_, row) => {
        const statusInfo = getStatusInfo(row);
        
        if (statusInfo.isProcessing) {
          return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}>
              <div className="animate-spin rounded-full h-3 w-3 border border-gray-500 border-t-transparent mr-1"></div>
              {statusInfo.label}
            </span>
          );
        }
        
        if (statusInfo.hasLink) {
          return (
            <a
              href={statusInfo.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 hover:opacity-80"
              title="Click to view pull request"
            >
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}>
                {statusInfo.label}
              </span>
              <ExternalLink className="w-4 h-4 text-gray-500" />
            </a>
          );
        }
        
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}>
            {statusInfo.label}
          </span>
        );
      },
      headerClassName: "w-1/7",
      cellClassName: "w-1/7"
    };

    const columns = [...baseColumns, ...dynamicColumns];
    
    // Only add actions column if showActions is not false
    if (integrationConfig?.features?.showActions !== false) {
      columns.push(actionsColumn);
    }
    
    // Only add status column if needed (currently always added, but can be conditional)
    columns.push(statusColumn);
    
    return columns;
  };

  // Action handlers using integration config
  const handleAction = async (actionConfig, finding, index) => {
    const findingId = getFindingId(finding, index);
    
    if (actionConfig.action === 'navigate') {
      // Handle navigation actions
      let path = actionConfig.path
        .replace('{id}', integrationId)
        .replace('{repository_id}', findingId)
        .replace('{finding_id}', findingId);
      navigate(path);
      return;
    }
    
    // Handle standard actions - all actions now use the generic handler for consistency
    switch (actionConfig.key) {
      case 'ignore_findings':
        setFindingToProcess({ finding, index });
        setConfirmDialogType('findings');
        setShowConfirmDialog(true);
        return;
      case 'unignore_findings':
        setFindingToProcess({ finding, index });
        setConfirmDialogType('unignore');
        setShowConfirmDialog(true);
        return;
      default:
        // Handle all API actions (create_ticket, resolve_ticket, move_to_vault, auto_fix, etc.)
        return actionsHook.handleGenericAction(actionConfig, finding, index);
    }
  };

  // Dialog handlers
  const handleConfirmAction = async () => {
    if (findingToProcess) {
      setShowConfirmDialog(false);
      if (confirmDialogType === 'findings') {
        // Get the ignore action config and use generic handler
        const ignoreAction = integrationConfig.actions.find(a => a.key === 'ignore_findings');
        if (ignoreAction) {
          await actionsHook.handleGenericAction(ignoreAction, findingToProcess.finding, findingToProcess.index);
        }
      } else if (confirmDialogType === 'unignore') {
        // Get the unignore action config and use generic handler
        const unignoreAction = integrationConfig.actions.find(a => a.key === 'unignore_findings');
        if (unignoreAction) {
          await actionsHook.handleGenericAction(unignoreAction, findingToProcess.finding, findingToProcess.index);
        }
      }
      setFindingToProcess(null);
      setConfirmDialogType('');
    }
  };

  const handleCancelAction = () => {
    setShowConfirmDialog(false);
    setFindingToProcess(null);
    setConfirmDialogType('');
  };

  // Handle bulk actions
  const handleBulkAction = async (actionConfig) => {
    if (findingsHook.selectedCount === 0) {
      showToast('Please select findings to perform bulk actions', 'error');
      return;
    }

    const selectedFindingsList = findingsHook.selectedFindings();
    await actionsHook.handleBulkAction(actionConfig, selectedFindingsList);
  };

  // Create columns
  const columns = createColumns();

  // If this is the S3 integration, delegate entirely to the specialized S3Findings
  // component so we get the exact same behavior and per-row handlers.
  // Temporarily commented out to test config-driven S3 actions
  // if (integrationType === 's3') {
  //   return <S3Findings />;
  // }

  // Early returns for invalid config, loading, or error states
  if (!integrationConfig) {
    return (
      <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
        <div className="mx-auto text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Integration Type</h2>
          <p className="text-gray-600 mb-4">The integration type "{integrationType}" is not supported.</p>
          <button
            onClick={() => navigate('/integration')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#441a9a] hover:bg-[#3a1080]"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Integrations
          </button>
        </div>
      </div>
    );
  }

  if (findingsHook.loading) {
    return (
      <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
        <div className="mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Link to="/integration" className="text-gray-700 hover:text-gray-900 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-black text-2xl md:text-3xl font-bold">
              Loading {integrationConfig?.displayName || 'Integration'} findings
            </h1>
          </div>

          <FilterShimmer />

          <div>
            <TableShimmer rows={8} />
          </div>
        </div>
      </div>
    );
  }

  if (findingsHook.error) {
    return (
      <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
        <div className="mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Link to="/integration" className="text-gray-700 hover:text-gray-900 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </div>
          <div className="text-center">
            <p className="text-black text-lg mb-4">{findingsHook.error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Get bulk actions from config
  const bulkActions = getBulkActions(integrationType);
  // Calculate filter counts from all findings data (works with mock data)
  // Use allFindings if available (for mock data), otherwise use findings.data (for API data)
  const filterCounts = filtersHook.getFilterCounts(
    findingsHook.allFindings && findingsHook.allFindings.length > 0 
      ? findingsHook.allFindings 
      : findingsHook.findings?.data || []
  );

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
      <div className="mx-auto">
        <div className="mb-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/identity-management">Identity Management</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={`/integration/${integrationType}`}>{integrationConfig?.displayName}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{findingsHook.name || integrationId}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <Link to={`/integration/${integrationType}`} className="text-gray-700 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-black text-2xl md:text-3xl font-bold">
            Findings for {findingsHook.name}
          </h1>
        </div>

        {/* Filter Section - exactly matching S3Findings */}
        <div className="py-3 border-gray-200 sticky top-0 z-10 bg-white">
          <div className="flex justify-between items-center gap-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => filtersHook.handleFilterChange('all')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    filtersHook.filterType === 'all'
                      ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                      : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                  }`}
                >
                  All ({filterCounts.all})
                </button>
                <button
                  onClick={() => filtersHook.handleFilterChange('verified')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    filtersHook.filterType === 'verified'
                      ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                      : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                  }`}
                >
                  {integrationConfig?.filterConfig?.filterLabels?.verified || 'Verified'} ({filterCounts.verified})
                </button>
                <button
                  onClick={() => filtersHook.handleFilterChange('unverified')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    filtersHook.filterType === 'unverified'
                      ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                      : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                  }`}
                >
                  {integrationConfig?.filterConfig?.filterLabels?.unverified || 'Unverified'} ({filterCounts.unverified})
                </button>
                <button
                  onClick={() => filtersHook.handleFilterChange('ignored')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    filtersHook.filterType === 'ignored'
                      ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                      : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                  }`}
                >
                  {integrationConfig?.filterConfig?.filterLabels?.ignored || 'Ignored'} ({filterCounts.ignored})
                </button>

                {/* Source Filter Dropdown */}
                <div className="source-dropdown-container relative">
                  <button
                    onClick={() => filtersHook.setShowSourceDropdown(!filtersHook.showSourceDropdown)}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors flex items-center space-x-1 ${
                      filtersHook.sourceFilter !== 'all'
                        ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                        : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                    }`}
                  >
                    <span>Source: {filtersHook.sourceFilter === 'all' ? 'All' : filtersHook.sourceFilter}</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>

                  {filtersHook.showSourceDropdown && (
                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-[#441a9a] z-20">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            filtersHook.handleSourceFilterChange('all');
                            filtersHook.setShowSourceDropdown(false);
                          }}
                          className={`flex items-center justify-between w-full px-4 py-2 text-left text-sm transition-colors ${
                            filtersHook.sourceFilter === 'all'
                              ? 'bg-[#f3effd] text-[#000000]'
                              : 'text-[#000000] hover:bg-[#f3effd]'
                          }`}
                        >
                          <span>All Sources</span>
                          <span className="text-xs text-gray-500">({findingsHook.filteredFindings.length})</span>
                        </button>
                        {filtersHook.getUniqueSourcesFromData(findingsHook.findings.data, filtersHook.filterType).map(source => {
                          const getSourceCount = () => {
                            return findingsHook.filteredFindings.filter(finding => finding.detector_type === source).length;
                          };

                          return (
                            <button
                              key={source}
                              onClick={() => {
                                filtersHook.handleSourceFilterChange(source);
                                filtersHook.setShowSourceDropdown(false);
                              }}
                              className={`flex items-center justify-between w-full px-4 py-2 text-left text-sm transition-colors ${
                                filtersHook.sourceFilter === source
                                  ? 'bg-[#f3effd] text-[#000000]'
                                  : 'text-[#000000] hover:bg-[#f3effd]'
                              }`}
                            >
                              <span>{source}</span>
                              <span className="text-xs text-gray-500">({getSourceCount()})</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bulk Actions */}
            {findingsHook.selectedCount > 0 && bulkActions.length > 0 && (
              <div className='flex gap-2'>
                {bulkActions.map(action => (
                  <button
                    key={action.key}
                    onClick={() => handleBulkAction(action)}
                    disabled={actionsHook.bulkActionLoading[action.key]}
                    className="px-3 py-1 rounded text-xs font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    {actionsHook.bulkActionLoading[action.key] && <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent"></div>}
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <DataTable
            columns={columns}
            data={findingsHook.filteredFindings}
            emptyMessage={`No ${integrationConfig?.displayName} findings found`}
            className="w-full border border-gray-300 rounded-lg"
            selectedRows={findingsHook.selected}
          />
        </div>

        {/* Pagination - exactly matching S3Findings */}
        <div className="flex flex-col sm:flex-row items-center justify-center my-6 gap-4 border-t border-gray-200 pt-6">
          <div className="flex items-center gap-2">
            <button
              onClick={findingsHook.handlePrevPage}
              disabled={!findingsHook.pagination.has_previous}
              className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                !findingsHook.pagination.has_previous
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-[#441a9a] text-white hover:bg-[#3a1080] border border-[#441a9a]"
              }`}
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-600">
                Page {findingsHook.pagination.page} of {findingsHook.pagination.last_page}
              </span>
            </div>
            <button
              onClick={findingsHook.handleNextPage}
              disabled={!findingsHook.pagination.has_next}
              className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                !findingsHook.pagination.has_next
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-[#441a9a] text-white hover:bg-[#3a1080] border border-[#441a9a]"
              }`}
            >
              Next
            </button>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Per page:</label>
            <select
              value={findingsHook.pagination.per_page}
              onChange={(e) => findingsHook.handlePerPageChange(Number(e.target.value))}
              className="px-3 py-2 rounded text-sm bg-white text-gray-700 border border-[#441a9a] focus:outline-none focus:ring-2 focus:ring-[#C0ACF3] focus:border-[#441a9a] hover:border-[#441a9a] transition-colors"
            >
              {[5, 10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Confirmation Dialog */}
        <ConfirmationDialog 
          show={showConfirmDialog}
          type={confirmDialogType}
          onConfirm={handleConfirmAction}
          onCancel={handleCancelAction}
        />
      </div>
    </div>
  );
};

export default IntegrationListPage;